import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { FunctionInfo } from '../types/UserRole';

/**
 * Store for managing user role permissions
 * This store holds the current user's role details and function access permissions
 * for their selected address system
 */

export interface UserPermissionState {
    roleId: number | null;
    roleName: string | null;
    addressSystemId: number | null;
    addressSystemName: string | null;
    
    // Map of function code to function info for quick lookup
    functionMap: Map<string, FunctionInfo>;
    
    // Actions
    setRolePermissions: (
        roleId: number,
        roleName: string,
        addressSystemId: number,
        addressSystemName: string,
        functions: FunctionInfo[]
    ) => void;
    
    clearPermissions: () => void;
    
    // Helper to check if user has access to a specific function
    hasAccess: (functionCode: string) => boolean;
}

const usePermissionStore = create<UserPermissionState>()(
    persist(
        (set, get) => ({
            roleId: null,
            roleName: null,
            addressSystemId: null,
            addressSystemName: null,
            functionMap: new Map(),

            setRolePermissions: (roleId, roleName, addressSystemId, addressSystemName, functions) => {
                const functionMap = new Map<string, FunctionInfo>();
                functions.forEach(func => {
                    functionMap.set(func.functionCode, func);
                });
                
                set({
                    roleId,
                    roleName,
                    addressSystemId,
                    addressSystemName,
                    functionMap
                });
            },

            clearPermissions: () => {
                set({
                    roleId: null,
                    roleName: null,
                    addressSystemId: null,
                    addressSystemName: null,
                    functionMap: new Map()
                });
            },

            hasAccess: (functionCode: string) => {
                const state = get();

                // Users with the ADMIN function code can access everything
                const adminAccess = state.functionMap.get('ADMIN');
                if (adminAccess?.hasAccess) {
                    return true;
                }

                const func = state.functionMap.get(functionCode);
                return func ? func.hasAccess : false;
            }
        }),
        {
            name: 'permission-store',
            version: 1,
            storage: createJSONStorage(() => sessionStorage), // Use sessionStorage for security
            // Custom serialization to handle Map
            partialize: (state) => ({
                roleId: state.roleId,
                roleName: state.roleName,
                addressSystemId: state.addressSystemId,
                addressSystemName: state.addressSystemName,
                // Convert Map to array for serialization
                functions: Array.from(state.functionMap.values())
            }),
            // Custom deserialization to restore Map
            merge: (persistedState: any, currentState) => {
                const functions = persistedState.functions || [];
                const functionMap = new Map<string, FunctionInfo>();
                functions.forEach((func: FunctionInfo) => {
                    functionMap.set(func.functionCode, func);
                });

                return {
                    ...currentState,
                    roleId: persistedState.roleId,
                    roleName: persistedState.roleName,
                    addressSystemId: persistedState.addressSystemId,
                    addressSystemName: persistedState.addressSystemName,
                    functionMap
                };
            }
        }
    )
);

export default usePermissionStore;
